import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  useTheme,
  useMediaQuery,
  Container
} from '@mui/material';
import { buildApiUrl } from '../config/api';
import { useToast } from '../hooks/useToast';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Area,
  AreaChart
} from 'recharts';
import { Skeleton } from '../components/ui/Skeleton';
import { Toast } from '../components/ui/Toast';
import { Breadcrumbs } from '../components/ui/Breadcrumbs';
import { StatCard } from '../components/ui/Card';
import {
  People as PeopleIcon,
  LocalOffer as OfferIcon
} from '@mui/icons-material';

// Types
interface UserStats {
  _id: string;
  count: number;
  providers: number;
  partners: number;
}

interface PromoCodeStats {
  _id: string;
  count: number;
  used: number;
  active: number;
  usageCount: number;
}

interface DashboardData {
  counts: {
    users: number;
    providers: number;
    partners: number;
    promoCodes: number;
    activePromoCodes: number;
    usedPromoCodes: number;
  };
  trends: {
    newUsers: number;
    newPromoCodes: number;
  };
  monthlyStats: {
    users: UserStats[];
    promoCodes: PromoCodeStats[];
  };
}

const Dashboard: React.FC = () => {
  const [userStats, setUserStats] = useState<UserStats[]>([]);
  const [promoStats, setPromoStats] = useState<PromoCodeStats[]>([]);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [period, setPeriod] = useState<string>('month');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Toast notifications
  const { toasts, removeToast, success, error: showError } = useToast();

  // Couleurs pour les graphiques
  const COLORS = ['#129baf', '#e99121', '#2ecc71', '#e74c3c'];

  useEffect(() => {
    loadAllStatistics();
  }, [period]);

  const loadAllStatistics = async () => {
    try {
      setLoading(true);
      setError('');

      const token = localStorage.getItem('@TikodAdmin:token');
      const headers = {
        'x-auth-token': token || '',
        'Content-Type': 'application/json'
      };

      // Récupération du tableau de bord
      const dashboardResponse = await fetch(buildApiUrl('/api/stats/dashboard'), {
        headers
      });

      // Récupération des statistiques utilisateurs
      const userResponse = await fetch(buildApiUrl(`/api/stats/users?period=${period}`), {
        headers
      });

      // Récupération des statistiques de codes promo
      const promoResponse = await fetch(buildApiUrl(`/api/stats/promocodes?period=${period}`), {
        headers
      });

      if (!dashboardResponse.ok || !userResponse.ok || !promoResponse.ok) {
        throw new Error('Erreur lors de la récupération des statistiques');
      }

      const dashboardData = await dashboardResponse.json();
      const userData = await userResponse.json();
      const promoData = await promoResponse.json();

      setDashboardData(dashboardData);
      setUserStats(userData);
      setPromoStats(promoData);
      success('Statistiques chargées avec succès');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erreur inconnue';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Préparation des données pour le graphique en secteurs
  const pieData = dashboardData ? [
    {
      name: 'Utilisateurs standards',
      value: dashboardData.counts.users - dashboardData.counts.providers - dashboardData.counts.partners
    },
    { name: 'Fournisseurs', value: dashboardData.counts.providers },
    { name: 'Partenaires', value: dashboardData.counts.partners }
  ] : [];

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Breadcrumbs items={[{ label: 'Tableau de bord', path: '/dashboard' }]} />
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(6, 1fr)' }, gap: 2, mb: 3 }}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Paper key={i} sx={{ p: 2, borderRadius: 3 }}>
              <Skeleton variant="circular" width={32} height={32} sx={{ mb: 2 }} />
              <Skeleton variant="text" width="60%" height={32} sx={{ mb: 1 }} />
              <Skeleton variant="text" width="80%" height={20} />
            </Paper>
          ))}
        </Box>
        <Paper sx={{ p: 3, mt: 3 }}>
          <Skeleton variant="text" width="40%" height={32} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" height={300} />
        </Paper>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Breadcrumbs items={[{ label: 'Tableau de bord', path: '/dashboard' }]} />
        <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'error.lighter' }}>
          <Typography variant="h6" color="error" gutterBottom>
            Erreur de chargement
          </Typography>
          <Typography color="error">{error}</Typography>
        </Paper>
      </Container>
    );
  }

  if (!dashboardData) {
    return (
      <Container maxWidth="lg">
        <Breadcrumbs items={[{ label: 'Tableau de bord', path: '/dashboard' }]} />
        <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'warning.lighter' }}>
          <Typography variant="h6" color="warning.main" gutterBottom>
            Aucune donnée disponible
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
        {/* Breadcrumbs */}
        <Breadcrumbs items={[{ label: 'Tableau de bord' }]} />

        {/* En-tête avec logo - Responsive */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          mb: { xs: 2, md: 4 },
          background: 'linear-gradient(135deg, #129baf 0%, #0e7989 100%)',
          color: 'white',
          p: { xs: 2, sm: 3 },
          borderRadius: 2,
          boxShadow: '0 4px 12px rgba(18, 155, 175, 0.3)',
          flexDirection: { xs: 'column', sm: 'row' },
          textAlign: { xs: 'center', sm: 'left' }
        }}>
          <img
            src="/images/tikod-logo.png"
            alt="TiKod Logo"
            style={{
              height: isMobile ? '36px' : '48px',
              width: 'auto',
              borderRadius: '8px',
              marginRight: isMobile ? '0' : '16px',
              marginBottom: isMobile ? '8px' : '0',
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
            }}
          />
          <Box>
            <Typography variant="h4" gutterBottom sx={{
              mb: 0.5,
              fontWeight: 'bold',
              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' }
            }}>
              Tableau de bord TiKod
            </Typography>
            <Typography variant="subtitle1" sx={{
              opacity: 0.9,
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }}>
              Vue d'ensemble de votre plateforme d'administration
            </Typography>
          </Box>
        </Box>

        {/* Cartes de résumé - Responsive */}
        <Box sx={{ mb: { xs: 2, md: 4 } }}>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, 1fr)',
              sm: 'repeat(3, 1fr)',
              md: 'repeat(6, 1fr)'
            },
            gap: { xs: 1, sm: 2 }
          }}>
            <StatCard
              title="Total Utilisateurs"
              value={dashboardData.counts.users}
              color="#129baf"
              trend={dashboardData.trends.newUsers}
              icon={<PeopleIcon />}
            />
            <StatCard
              title="Fournisseurs"
              value={dashboardData.counts.providers}
              color="#2ecc71"
              icon={<PeopleIcon />}
            />
            <StatCard
              title="Partenaires"
              value={dashboardData.counts.partners}
              color="#e99121"
              icon={<PeopleIcon />}
            />
            <StatCard
              title="Codes Promo"
              value={dashboardData.counts.promoCodes}
              color="#e74c3c"
              trend={dashboardData.trends.newPromoCodes}
              icon={<OfferIcon />}
            />
            <StatCard
              title="Codes Actifs"
              value={dashboardData.counts.activePromoCodes}
              color="#00796b"
              icon={<OfferIcon />}
            />
            <StatCard
              title="Codes Utilisés"
              value={dashboardData.counts.usedPromoCodes}
              color="#f39c12"
              icon={<OfferIcon />}
            />
          </Box>
        </Box>

        {/* Contrôles */}
        <Paper sx={{ p: { xs: 2, sm: 3 }, mb: { xs: 2, md: 3 } }}>
          <Typography variant="h6" gutterBottom sx={{
            fontSize: { xs: '1rem', sm: '1.25rem' }
          }}>
            Contrôles d'affichage
          </Typography>
          <FormControl sx={{ minWidth: { xs: 150, sm: 200 } }}>
            <InputLabel>Période</InputLabel>
            <Select
              value={period}
              label="Période"
              onChange={(e) => setPeriod(e.target.value)}
              size={isMobile ? 'small' : 'medium'}
            >
              <MenuItem value="day">Jour</MenuItem>
              <MenuItem value="week">Semaine</MenuItem>
              <MenuItem value="month">Mois</MenuItem>
            </Select>
          </FormControl>
        </Paper>

        {/* Graphiques - Stack responsive */}
        <Stack spacing={{ xs: 2, md: 3 }}>
          {/* Première ligne - Évolution et répartition des utilisateurs */}
          <Box sx={{ 
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
            gap: { xs: 2, md: 3 }
          }}>
            <Paper sx={{ p: { xs: 2, sm: 3 }, height: 'fit-content', flex: 1 }}>
              <Typography variant="h6" gutterBottom sx={{
                fontSize: { xs: '1rem', sm: '1.25rem' }
              }}>
                Évolution des utilisateurs
              </Typography>
              <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
                <AreaChart data={userStats}>
                  <defs>
                    <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#129baf" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#129baf" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="_id" 
                    fontSize={isMobile ? 10 : 12}
                    interval={isMobile ? 1 : 0}
                  />
                  <YAxis fontSize={isMobile ? 10 : 12} />
                  <Tooltip />
                  {!isMobile && <Legend />}
                  <Area type="monotone" dataKey="count" stroke="#129baf" fillOpacity={1} fill="url(#colorUsers)" name="Total" />
                  <Line type="monotone" dataKey="providers" stroke="#2ecc71" name="Fournisseurs" />
                  <Line type="monotone" dataKey="partners" stroke="#e99121" name="Partenaires" />
                </AreaChart>
              </ResponsiveContainer>
            </Paper>

            <Paper sx={{ p: { xs: 2, sm: 3 }, height: 'fit-content', flex: 1 }}>
              <Typography variant="h6" gutterBottom sx={{
                fontSize: { xs: '1rem', sm: '1.25rem' }
              }}>
                Répartition des utilisateurs
              </Typography>
              <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={isMobile ? false : ({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={isMobile ? 60 : 80}
                    fill="#129baf"
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  {isMobile && <Legend />}
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Box>

          {/* Statistiques des codes promo */}
          <Paper sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" gutterBottom sx={{
              fontSize: { xs: '1rem', sm: '1.25rem' }
            }}>
              Statistiques des codes promo par {period === 'day' ? 'jour' : period === 'week' ? 'semaine' : 'mois'}
            </Typography>
            <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
              <BarChart data={promoStats}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="_id" 
                  fontSize={isMobile ? 10 : 12}
                  interval={isMobile ? 1 : 0}
                />
                <YAxis fontSize={isMobile ? 10 : 12} />
                <Tooltip />
                {!isMobile && <Legend />}
                <Bar dataKey="count" fill="#e74c3c" name="Créés" />
                <Bar dataKey="used" fill="#2ecc71" name="Utilisés" />
                <Bar dataKey="active" fill="#e99121" name="Actifs" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>

          {/* Évolution mensuelle globale */}
          {dashboardData.monthlyStats && (
            <Paper sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography variant="h6" gutterBottom sx={{
                fontSize: { xs: '1rem', sm: '1.25rem' }
              }}>
                Tendances mensuelles (6 derniers mois)
              </Typography>
              <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
                <LineChart data={dashboardData.monthlyStats.users}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="_id"
                    fontSize={isMobile ? 10 : 12}
                    interval={isMobile ? 1 : 0}
                  />
                  <YAxis fontSize={isMobile ? 10 : 12} />
                  <Tooltip />
                  {!isMobile && <Legend />}
                  <Line type="monotone" dataKey="count" stroke="#129baf" name="Nouveaux utilisateurs" strokeWidth={2} />
                  <Line type="monotone" dataKey="providers" stroke="#2ecc71" name="Nouveaux fournisseurs" />
                  <Line type="monotone" dataKey="partners" stroke="#e99121" name="Nouveaux partenaires" />
                </LineChart>
              </ResponsiveContainer>
            </Paper>
          )}
        </Stack>
      </Box>

      {/* Toast notifications */}
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          open={true}
          onClose={() => removeToast(toast.id)}
          message={toast.message}
          severity={toast.severity}
        />
      ))}
    </Container>
  );
};

export default Dashboard; 